import React, { useEffect, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { PieChart, Pie, Cell } from "recharts";
import { fetchContactsOverviewCard } from "../../../../redux/Slice/Dashboard/dashboardSlice";

// Same 3 ring colors this card has always used (kept as-is per Rahim's ask,
// 2026-08-18 -- only the underlying metric/labels changed to Assigned/
// Unassigned/Closed, not the palette). Deliberately NOT the app's STATUS_COLOR
// (constants.js) -- this card keeps its own distinct look.
const SEGMENT_COLORS = { Assigned: "#a855f7", Unassigned: "#f97316", Closed: "#06b6d4" };

function ContactsOverviewCard() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const ringGap = 9;
  const ringThickness = 16;
  const outerMost = 135;

  const summary = useSelector((state) => state.dashboard.contactsOverviewCard);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const authWorkspaceId = useSelector((state) => state.auth?.user?.workspaceId);
  const workspaceId = currentWorkspace?._id || authWorkspaceId;

  // No `range` dependency anymore (2026-08-18) -- see fetchContactsOverviewCard's
  // own comment. Still gated on workspaceId so it doesn't fire before auth/workspace
  // is ready, and still refetches on workspace switch.
  useEffect(() => {
    if (!workspaceId) return;
    dispatch(fetchContactsOverviewCard());
  }, [dispatch, workspaceId]);

  const total = summary?.total || 0;
  // Assigned/Unassigned/Closed are genuinely mutually exclusive (every contact is
  // exactly one of the three) and always sum to `total` -- unlike the old Engaged/
  // Created/Closed metrics, this percentage is always a real, honest fraction of
  // the whole, never clamped/guarded against exceeding 100.
  const data = useMemo(() => {
    const toPct = (count) => (total > 0 ? Math.round(((count || 0) / total) * 100) : 0);
    return [
      { name: "Assigned", pct: toPct(summary?.assigned), color: SEGMENT_COLORS.Assigned },
      { name: "Unassigned", pct: toPct(summary?.unassigned), color: SEGMENT_COLORS.Unassigned },
      { name: "Closed", pct: toPct(summary?.closed), color: SEGMENT_COLORS.Closed },
    ];
  }, [summary, total]);

  return (
    <Box
      sx={{
        flex: 1,
        maxWidth: 540,
        height: 440,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "24px",
        p: 3,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.875rem" }}>
        <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>Contacts Overview</Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4.5 }}>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ position: "relative", width: outerMost * 2, height: outerMost * 2 }}>
          <PieChart width={outerMost * 2} height={outerMost * 2}>
            {data.map((ring, i) => {
              const outerRadius = outerMost - i * (ringThickness + ringGap);
              const innerRadius = outerRadius - ringThickness;
              return (
                <Pie
                  key={ring.name}
                  data={[{ value: ring.pct }, { value: 100 - ring.pct }]}
                  dataKey="value"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                  cornerRadius={ringThickness / 2}
                  isAnimationActive
                  animationBegin={i * 150}
                  animationDuration={900}
                  animationEasing="ease-out"
                >
                  <Cell fill={ring.color} />
                  <Cell fill="transparent" />
                </Pie>
              );
            })}
          </PieChart>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "1.75rem", lineHeight: 1.2 }}>
              {data[0].pct}%
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>of {total} Contacts</Typography>
          </Box>
        </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 3 }}>
          {data.map((ring) => (
            <Box key={ring.name} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: ring.color }} />
              <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                {ring.name} <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>{ring.pct}%</Box>
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default ContactsOverviewCard;
